import { NOM_FORMULE, type Formule } from '@repere/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { ChoisirFormule } from '../abonnement/ChoisirFormule';
import { dateComplete } from '../dossier/dates';
import { nomDuDossier } from '../dossier/prenoms';
import { messageDErreur, useSession } from '../auth/session';
import { useTraduction } from '../i18n/langue';
import { Alerte, Bouton, Carte } from '../ui';
import type { DossierEnCarte } from '../accueil/CarteDossier';

/** La situation du compte, telle que l'API la rend (`GET /billing/subscription`). */
interface Situation {
  acces: 'gratuit' | 'abonne' | 'couvert' | 'professionnel';
  formule: Formule;
  statut: 'none' | 'trialing' | 'active' | 'past_due' | 'canceled';
  origine: 'stripe' | 'code_essai' | 'geste' | null;
  debut: string | null;
  fin: string | null;
  resiliationProgrammee: boolean;
  essaiJusquau: string | null;
  facturesDisponibles: boolean;
}

/** Une ligne « intitulé : valeur » de la fiche. */
function Ligne({ intitule, children }: { intitule: string; children: ReactNode }) {
  return (
    <p className="compte-ligne">
      <span className="compte-intitule">{intitule}</span>
      <span>{children}</span>
    </p>
  );
}

/**
 * « Mon compte » : où en est l'abonnement, et qui est en face dans chaque dossier.
 *
 * L'état de l'abonnement ne se lisait nulle part. Le badge de l'en-tête donne un mot —
 * « Solo » — et l'écran d'abonnement invite à souscrire ; entre les deux, personne ne
 * pouvait dire depuis quand il paie, jusqu'à quand son accès court, ni où trouver ses
 * factures. C'est peu de chose jusqu'au jour où l'on doit justifier une dépense.
 *
 * Deux règles tiennent cet écran, et elles viennent du produit, pas de l'ergonomie :
 *
 * - **Un compte couvert n'apprend pas qui paie.** Il lit que son écriture est ouverte par
 *   une formule Duo souscrite sur un dossier partagé, et jusqu'à quand — jamais par qui.
 *   C'est la première règle du duo, et le serveur la tient déjà (`origine` et `debut` ne
 *   sont rendus que pour un abonnement propre) ; l'écran ne fait que ne pas la relâcher.
 * - **Les factures ne sont pas réimplémentées.** Le bouton ouvre le portail Stripe, qui
 *   tient les factures, le moyen de paiement et la résiliation. Le bouton n'apparaît que
 *   si le portail peut réellement s'ouvrir : un bouton qui mène à une erreur vaut moins
 *   que pas de bouton du tout.
 *
 * Les adresses des deux parents figurent sur la fiche de chaque dossier. Elles ne sont un
 * secret ni pour l'un ni pour l'autre — ils se sont invités par e-mail — et savoir à qui
 * écrire quand l'application ne suffit plus fait partie de ce qu'un compte doit dire.
 */
export function MonCompte({ surDossier }: { surDossier?: (id: string) => void }) {
  const { client, profil } = useSession();
  const { messages } = useTraduction();
  const m = messages.compte;

  const situation = useQuery({
    queryKey: ['situation'],
    queryFn: () => client.request<Situation>('/billing/subscription'),
  });

  const dossiers = useQuery({
    queryKey: ['case-files'],
    queryFn: () => client.request<DossierEnCarte[]>('/case-files'),
  });

  const factures = useMutation({
    mutationFn: () => client.request<{ url: string }>('/billing/portal', { method: 'POST' }),
    // Le portail est une page de Stripe : on quitte l'application, on n'en encadre pas une
    // seconde dans une nôtre. `assign` et non `open` — un onglet ouvert par script se fait
    // bloquer, et le parent n'aurait aucune idée de ce qui s'est passé.
    onSuccess: (resultat) => globalThis.location.assign(resultat.url),
  });

  const etat = situation.data;

  return (
    <>
      {/* Le titre s'écrit désormais dans le bandeau, à la place du mot-symbole
          (`Coquille.tsx`, `TITRES_STATIQUES`). Il reste ici comme `<h1>` invisible : le
          plan du document ne doit pas venir d'un bandeau qui vit hors du `<main>` et se
          répète d'un écran à l'autre. Le dessin change, le plan ne change pas. */}
      <h1 className="invisible">{m.monCompte}</h1>

      <Carte titre={m.identiteTitre}>
        <Ligne intitule="Adresse">{profil?.email ?? '—'}</Ligne>
        {profil?.displayName ? <Ligne intitule="Prénom">{profil.displayName}</Ligne> : null}

        {etat ? (
          <>
            <Ligne intitule={m.formule}>{NOM_FORMULE[etat.formule]}</Ligne>
            <Ligne intitule={m.situation}>{m.statuts[etat.statut]}</Ligne>
            {etat.debut ? (
              <Ligne intitule={m.debut}>{dateComplete(new Date(etat.debut))}</Ligne>
            ) : null}
            {etat.fin ? (
              <Ligne intitule={m.echeance}>{dateComplete(new Date(etat.fin))}</Ligne>
            ) : null}
            {etat.origine ? <Ligne intitule={m.origine}>{m.origines[etat.origine]}</Ligne> : null}

            {etat.essaiJusquau ? (
              <p className="discret">{m.essaiJusquau(dateComplete(new Date(etat.essaiJusquau)))}</p>
            ) : null}
            {etat.resiliationProgrammee ? (
              <p className="discret">{m.resiliationProgrammee}</p>
            ) : null}
            {etat.acces === 'couvert' ? <p className="discret">{m.accesCouvert}</p> : null}
            {etat.acces === 'professionnel' ? (
              <p className="discret">{m.accesProfessionnel}</p>
            ) : null}

            {/* Le même chemin que dans l'en-tête du dossier, et pour la même raison : c'était
                jusqu'ici le seul endroit où le trouver, et « Mon compte » — l'écran qu'on
                ouvre justement pour savoir où en est son abonnement — n'en proposait aucun.
                `ChoisirFormule` se tait déjà de lui-même pour un compte non gratuit. */}
            <ChoisirFormule />

            {factures.isError ? (
              <Alerte titre={m.erreurFactures}>{messageDErreur(factures.error)}</Alerte>
            ) : null}

            {etat.facturesDisponibles ? (
              <Bouton onClick={() => factures.mutate()} disabled={factures.isPending}>
                {factures.isPending ? m.ouvertureFactures : m.factures}
              </Bouton>
            ) : (
              <p className="discret" style={{ marginBottom: 0 }}>
                {m.facturesIndisponibles}
              </p>
            )}
          </>
        ) : null}
      </Carte>

      <Carte titre={m.dossiersTitre}>
        {dossiers.data && dossiers.data.length === 0 ? (
          <p className="discret" style={{ margin: 0 }}>
            {m.aucunDossier}
          </p>
        ) : null}

        {(dossiers.data ?? []).map((dossier) => {
          const titre = nomDuDossier(dossier.label, dossier.children, dossier.custodyKind);
          // Les deux emplacements, dans l'ordre — et celui qui reste libre le dit. Une
          // liste qui n'afficherait que les présents laisserait croire à un dossier à un
          // seul parent là où il y a une place qui attend.
          const emplacements = [1, 2].map((rang) => ({
            rang,
            parent: dossier.parents.find((p) => p.slot === rang) ?? null,
          }));

          return (
            <section key={dossier.id} className="compte-dossier">
              <h3>
                {surDossier ? (
                  <Bouton variante="lien" onClick={() => surDossier(dossier.id)}>
                    {titre}
                  </Bouton>
                ) : (
                  titre
                )}
              </h3>
              {emplacements.map(({ rang, parent }) => (
                <Ligne key={rang} intitule={m.parent(rang)}>
                  {parent ? (
                    <>
                      {parent.displayName}
                      {parent.email ? <span className="discret"> · {parent.email}</span> : null}
                    </>
                  ) : (
                    <span className="discret">{m.placeLibre}</span>
                  )}
                </Ligne>
              ))}
            </section>
          );
        })}
      </Carte>
    </>
  );
}
